import React from 'react';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useCollections } from './hooks/useCollections';
import { useApplications } from './hooks/useApplications';
import { useNavigation } from './hooks/useNavigation';
import PortalTopNavigation from '../../components/TopNavigation/TopNavigation';
import PortalHeader from './components/PortalHeader/PortalHeader';
import HowItWorksSection from './components/HowItWorksSection/HowItWorksSection';
import DataSourcesSection from './components/DataSourcesSection/DataSourcesSection';
import ApplicationsSection from './components/ApplicationsSection/ApplicationsSection';
import EmptyState from './components/common/EmptyState';
import './HomePage.scss';

const HomePage: React.FC = () => {
  const { collections, loading: collectionsLoading, error: collectionsError } = useCollections();
  const { applications, loading: applicationsLoading, error: applicationsError } = useApplications();
  const { handleCollectionClick, handleApplicationClick, handleManageCollections, handleManageApplications } = useNavigation();

  const error = collectionsError || applicationsError;

  // Calculate stats from collections
  const stats = React.useMemo(() => {
    const totalItems = collections.reduce((sum, col) => sum + (col.itemCount || 0), 0);
    const uniqueSources = new Set(collections.map((col) => col.source));
    return {
      totalItems,
      totalSources: uniqueSources.size,
      totalCollections: collections.length,
    };
  }, [collections]);

  if (error) {
    return (
      <div className="portal-page">
        <PortalTopNavigation />
        <Box padding={{ top: 'xxxl', horizontal: 'l' }}>
          <EmptyState title="Error Loading Portal" description={error} icon="status-warning" />
        </Box>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <PortalTopNavigation />
      <div className="portal-content">
        <SpaceBetween size="xxl">
          <PortalHeader
            title="Build content collections freely, subscribe and use instantly."
            subtitle={
              collectionsLoading
                ? 'Loading data...'
                : `${stats.totalSources} data sources containing ${stats.totalItems.toLocaleString()} total content items.`
            }
          />

          <HowItWorksSection onManageCollections={handleManageCollections} onManageApplications={handleManageApplications} />

          <DataSourcesSection
            collections={collections}
            stats={stats}
            loading={collectionsLoading}
            onCollectionClick={handleCollectionClick}
          />

          <ApplicationsSection applications={applications} loading={applicationsLoading} onApplicationClick={handleApplicationClick} />
        </SpaceBetween>
      </div>
    </div>
  );
};

export default HomePage;
